import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { DataStorageService } from "../../../services/data-storage.service";
import { Subject } from "rxjs";
import { PetInsuranceData } from "../../../models";
import { PetCardsVisibilityService } from "../../../services/pet-cards-visibility.service";
import { TranslateService } from "@ngx-translate/core";
import { animate, style, transition, trigger } from "@angular/animations";
import { ApiHttpService } from "../../../services/api-http.service";
import { environment } from "../../../../environments/environment";
import { LoadingService } from "../../../services/loading.service";

/** Minimum upward drag, in px, before a swipe collapses the cards. */
const VERTICAL_SWIPE_THRESHOLD = 30;

@Component({
  selector: 'cpf-pets-carousel',
  templateUrl: './pets-carousel.component.html',
  styleUrls: ['./pets-carousel.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: 0, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '*' }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: 0 }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PetsCarouselComponent implements OnInit {
  @ViewChild('cardCarousel', { static: false }) cardCarousel!: ElementRef<HTMLElement>;
  @Output() actionButtonClickEmitter: EventEmitter<PetInsuranceData> = new EventEmitter<PetInsuranceData>();

  public activeStep = 0;
  public pets: any[] = [];

  public showPetCards = true;
  public flipCard = false;

  private initialX: number | null = null;
  private initialY: number | null = null;

  private changeActivePet$ = new Subject<any>();


  constructor(private dataStorageService: DataStorageService,
              private router: Router,
              private petCardsVisibilityService: PetCardsVisibilityService,
              private translateService: TranslateService,
              private apiHttpService: ApiHttpService,
              private loadingService: LoadingService) {
    this.dataStorageService.loaded$.subscribe((loaded) => {
      if (loaded) {
        this.pets = this.dataStorageService.getAllData;
      }
    });

    this.changeActivePet$.subscribe(() => {
      const newSelectedPet = this.dataStorageService.getAllData[this.activeStep];
      if (!newSelectedPet) {
        return;
      }
      this.dataStorageService.selectedData = newSelectedPet;
    });

    this.dataStorageService.selectedData$.subscribe(data => {
      this.flipCard = false;
    })
  }

  ngOnInit() {
    setTimeout(() => {
      this.selectStep(this.activeStep);
    }, 100);

    this.petCardsVisibilityService.hidePetCards$.subscribe(hidden => {
      this.showPetCards = !hidden;
    });
  }

  selectStep(step: number) {
    if (!this.showPetCards) {
      return;
    }

    this.activeStep = step;

    this.changeActivePet$.next(step);

    var slider = this.cardCarousel.nativeElement;

    for (var i = 0; i < slider.childElementCount; i++) {
      var card = slider.children[i];
      card.classList.remove('left');
      card.classList.remove('back');
      card.classList.remove('delay');
      card.classList.remove('no-delay');
      var cardStep = Number.parseInt(card.getAttribute("data-step") || '10');
      cardStep = i - step;
      card.setAttribute("data-step", String(cardStep));

      if (cardStep < 0) {
        card.classList.add('left');
      } else if (cardStep > 2) {
        card.classList.add('back');
      }
    }
  }

  nextSlide() {
    if (!this.showPetCards) {
      return;
    }

    var slider = this.cardCarousel.nativeElement;

    var secondCard = slider.querySelector('[data-step="1"]');

    if (!secondCard) {
      return;
    }

    this.selectStep(this.activeStep + 1);
  }

  prevSlide() {
    if (!this.showPetCards) {
      return;
    }

    var slider = this.cardCarousel.nativeElement;

    var leftCard = slider.querySelector('[data-step="-1"]');

    if (!leftCard) {
      return;
    }

    this.selectStep(this.activeStep - 1);
  }

  startTouch(e: TouchEvent) {
    this.initialX = e.touches[0].clientX;
    this.initialY = e.touches[0].clientY;
  }

  moveTouch(e: TouchEvent) {
    if (this.initialX === null) {
      return;
    }

    if (this.initialY === null) {
      return;
    }

    var currentX = e.touches[0].clientX;
    var currentY = e.touches[0].clientY;

    var diffX = this.initialX - currentX;
    var diffY = this.initialY - currentY;

    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX > 0) {
        this.nextSlide()
      } else {
        this.prevSlide()
      }
    } else {
      // Swiping up collapses the cards; bringing them back is the chevron's job. Unlike the
      // horizontal slide this needs a deliberate drag, otherwise the jitter of a tap-to-flip
      // would hide the carousel. diffY > 0 means the finger moved up, so a downward swipe
      // fails this check too.
      if (diffY < VERTICAL_SWIPE_THRESHOLD) {
        return;
      }

      this.petCardsVisibilityService.setHiddenByUser(true);
    }

    this.initialX = null;
    this.initialY = null;

    e.preventDefault();
  }

  endTouch() {
    this.initialX = null;
    this.initialY = null;
  }

  cardAction() {
    this.router.navigate(['calingo', 'fnol']);
  }

  cardClicked() {
    this.flipCard = !this.flipCard;
  }

  async openClosingPage() {
    const lang = this.translateService.getCurrentLang();
    const loader = this.loadingService.open();
    let result = null;
    try {
      result = await this.apiHttpService.createClosingSession();
    } finally {
      loader.close();
    }
    const baseUrl = `${environment.petClosingUrl}/#/pet-info?lang=${lang}`;
    const url = result?.sessionId ? `${baseUrl}&sessionId=${result.sessionId}` : baseUrl;
    window.open(url, '_blank');
  }
}
